<template>
  <b-modal
      ref="cmModal"
      centered
      size="lg"
      modal-class="comment-modal"
      header-class="comment-modal__header"
      body-class="comment-modal__body"
      footer-class="comment-modal__footer"
      hide-header-close
      :no-close-on-backdrop="isBusy"
      :no-close-on-esc="isBusy"
      @hidden="onHidden"
      @shown="onShown"
  >
    <template #modal-title>
      <div class="cm-title">{{ isEditing ? "ویرایش نظر" : "ایجاد نظر جدید" }}</div>
    </template>

    <b-form @submit.prevent>
      <!-- بخش‌ها زیر هم با خط جداکننده -->
      <div class="cm-sections">
        <section class="cm-block">
          <h6 class="cm-block__title">کاربر</h6>

          <div class="cm-user-row">
            <!-- دایره‌ی آواتار: کلیک → انتخاب عکس → آپلود خودکار با حلقه‌ی پیشرفت دور دایره -->
            <div class="cm-avatar-box">
              <div
                  class="cm-avatar-upload"
                  :class="{ 'is-uploading': avatar.isBtnsDisabled, 'has-image': !!form.imageUser }"
                  role="button"
                  tabindex="0"
                  :aria-label="form.imageUser ? 'تغییر آواتار' : 'آپلود آواتار'"
                  :title="form.imageUser ? 'برای تغییر کلیک کنید' : 'برای آپلود کلیک کنید'"
                  @click="pickAvatar"
                  @keydown.enter.prevent="pickAvatar"
              >
                <img v-if="form.imageUser" :src="form.imageUser" class="cm-avatar-img" alt="avatar" />
                <svg v-else viewBox="0 0 24 24" class="cm-avatar-upload__icon" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                  <path d="M12 16V4M6 10l6-6 6 6M4 20h16" />
                </svg>
                <svg v-if="avatar.isBtnsDisabled" class="cm-avatar-ring" viewBox="0 0 64 64" aria-hidden="true">
                  <circle class="cm-avatar-ring__track" cx="32" cy="32" r="30" />
                  <circle class="cm-avatar-ring__bar" cx="32" cy="32" r="30" :style="{ strokeDashoffset: avatarRingOffset }" />
                </svg>
              </div>
              <input ref="avatarInput" type="file" accept="image/*" class="d-none" @change="onAvatarFile" />
              <button
                  v-if="form.imageUser"
                  type="button"
                  class="cm-preview-close"
                  :disabled="avatar.isBtnsDisabled || isBusy"
                  @click="removeAvatarUploaded"
              >
                ×
              </button>
            </div>

            <b-form-group label="نام" label-for="c-firstName" label-class="cm-label" class="cm-group cm-user-row__field">
              <b-form-input
                  id="c-firstName"
                  v-model.trim="form.firstName"
                  class="cm-control"
                  :class="{ 'cm-control--invalid': !!errors.firstName }"
                  placeholder="نام"
                  autocomplete="off"
                  :disabled="isBusy"
              />
              <div v-if="errors.firstName" class="cm-error">{{ errors.firstName }}</div>
            </b-form-group>

            <b-form-group label="نام خانوادگی" label-for="c-lastName" label-class="cm-label" class="cm-group cm-user-row__field">
              <b-form-input
                  id="c-lastName"
                  v-model.trim="form.lastName"
                  class="cm-control"
                  placeholder="نام خانوادگی"
                  autocomplete="off"
                  :disabled="isBusy"
              />
            </b-form-group>
          </div>

          <div class="cm-avatar-presets">
            <div class="cm-presets-title">انتخاب از تصاویر پیشنهادی</div>
            <div class="cm-presets">
              <img
                v-for="(p, idx) in avatarPresets"
                :key="idx"
                :src="p"
                class="cm-preset-img"
                :class="{ 'cm-preset--selected': form.imageUser === p, 'cm-preset--disabled': isBusy }"
                @click="selectPreset(p)"
                alt="preset"
              />
            </div>
          </div>

        </section>

        <section class="cm-block">
          <h6 class="cm-block__title">نظر</h6>

          <!-- موضوع، مورد، اولویت و VIP در یک ردیف -->
          <div class="cm-subject-row">
            <div>
              <label for="c-type" class="cm-label">موضوع</label>
              <v-select
                  id="c-type"
                  label="name"
                  v-model="form.type"
                  :options="commentTypeOptions"
                  :reduce="(val) => val.value"
                  :clearable="false"
                  :disabled="isBusy"
                  class="cm-vselect"
              />
            </div>

            <div class="cm-subject-row__object" :class="{ 'is-invalid': !!errors.objectId }">
              <div class="cm-label">{{ objectFieldLabel }}</div>
              <div v-if="Number(form.objectId) > 0 && !showPicker" class="cm-picker-selected">
                <div class="cm-picker-selected__text">{{ selectedObjectLabel }}</div>
                <button
                    type="button"
                    class="cm-picker-selected__clear"
                    :disabled="isBusy"
                    aria-label="تغییر"
                    @click="clearObjectSelection"
                >
                  ×
                </button>
              </div>
              <template v-else>
                <MasterPicker v-if="Number(form.type) === 5" @getMaster="setSelectedMaster" />
                <LessonCategoryPicker
                    v-else-if="Number(form.type) === 6"
                    closeOnSelect
                    @getLessonCategory="setSelectedPackageCategory"
                />
                <LessonPicker v-else-if="Number(form.type) === 3" closeOnSelect @getLesson="setSelectedLesson" />
              </template>
              <div v-if="errors.objectId" class="cm-error">{{ errors.objectId }}</div>
            </div>

            <div>
              <label for="c-priority" class="cm-label">اولویت</label>
              <b-form-input id="c-priority" type="number" min="0" v-model.number="form.priority" class="cm-control" :disabled="isBusy" />
            </div>

            <div class="cm-subject-row__vip">
              <div class="cm-label">VIP</div>
              <b-form-checkbox switch v-model="form.isVip" :disabled="isBusy" aria-label="نمایش به‌عنوان VIP" />
            </div>
          </div>
          <div v-if="errors.priority" class="cm-error">{{ errors.priority }}</div>

          <div class="cm-label mt-1">نوع نظر</div>
          <div class="cm-kind" role="radiogroup" aria-label="نوع نظر">
            <button
                v-for="kind in commentKinds"
                :key="String(kind.value)"
                type="button"
                role="radio"
                class="cm-kind__btn"
                :class="{ 'cm-kind__btn--active': (form.mediaType || null) === kind.value }"
                :aria-checked="(form.mediaType || null) === kind.value ? 'true' : 'false'"
                :disabled="isBusy"
                @click="form.mediaType = kind.value"
            >
              {{ kind.text }}
            </button>
          </div>

          <b-form-group label="متن" label-for="c-message" label-class="cm-label" class="cm-group mt-1">
            <b-form-textarea
                id="c-message"
                rows="2"
                v-model.trim="form.message"
                class="cm-control cm-textarea"
                :class="{ 'cm-control--invalid': !!errors.content }"
                :placeholder="canUploadMedia ? 'توضیح کوتاه (اختیاری)' : 'متن نظر را وارد کنید...'"
                :disabled="isBusy"
            />
            <div v-if="errors.content" class="cm-error">{{ errors.content }}</div>
          </b-form-group>

          <!-- فقط برای نظر تصویری، صوتی یا ویدیویی -->
          <template v-if="canUploadMedia">
            <div v-if="effectiveMediaUrl" class="cm-current-media">
              <span class="cm-chip">{{ effectiveMediaLabel }} فعلی</span>
              <span class="cm-current-media__hint">در بخش «شکل نهایی» پایین همین فرم دیده یا پخش می‌شود.</span>
              <button type="button" class="cm-preview__remove" :disabled="isBusy" @click="clearMediaFile">
                حذف {{ effectiveMediaLabel }}
              </button>
            </div>
            <FileUploader
                v-else
                :title="`آپلود ${mediaLabel}`"
                :subtitle="mediaSubtitle"
                :accept="fileAccept"
                :file="media.file"
                :uploaded="media.uploaded"
                :uploaded-bytes="media.uploadedBytes"
                :total-size="media.totalSize"
                :speed-span="media.speedSpan"
                :eta-span="media.etaSpan"
                :done="media.done"
                :error-alert="media.errorAlert"
                :is-btns-disabled="media.isBtnsDisabled || isBusy"
                @select="onMediaSelect"
                @upload="uploadMedia"
                @reset="resetMedia"
            />
            <div v-if="errors.mediaType" class="cm-error">{{ errors.mediaType }}</div>
          </template>
        </section>

        <section ref="previewSection" class="cm-block">
          <h6 class="cm-block__title">شکل نهایی</h6>
          <!-- پیش‌نمایش همان چیزی که در سایت دیده می‌شود -->
          <div class="cm-final">
            <div class="cm-final__head">
              <img v-if="form.imageUser" :src="form.imageUser" class="cm-final__avatar" alt="" />
              <div v-else class="cm-final__avatar cm-avatar-placeholder"><span class="cm-avatar-placeholder__icon">👤</span></div>
              <div class="cm-final__who">
                <div class="cm-final__name">
                  {{ [form.firstName, form.lastName].filter(Boolean).join(" ") || "بدون نام" }}
                  <span v-if="form.isVip" class="cm-final__vip">VIP</span>
                </div>
                <div class="cm-final__meta">{{ selectedObjectLabel || "موضوع انتخاب نشده" }}</div>
              </div>
            </div>

            <div v-if="effectiveMediaUrl" class="cm-final__media">
              <img v-if="Number(effectiveMediaType) === 2" :src="effectiveMediaUrl" alt="" />
              <audio v-else-if="Number(effectiveMediaType) === 3" class="w-100" controls :src="effectiveMediaUrl" />
              <video v-else-if="Number(effectiveMediaType) === 4" controls :src="effectiveMediaUrl" />
            </div>

            <p v-if="form.message" class="cm-final__text">{{ form.message }}</p>
            <p v-if="!form.message && !effectiveMediaUrl" class="cm-hint mb-0">هنوز متن یا مدیایی وارد نشده است.</p>
          </div>
        </section>
      </div>
    </b-form>

    <template #modal-footer>
      <div class="cm-footer">
        <b-button type="button" variant="outline-secondary" class="cm-btn cm-btn--ghost" :disabled="isBusy" @click="closeModal">
          انصراف
        </b-button>

        <b-button type="button" variant="success" class="cm-btn cm-btn--success" :disabled="submitDisabled" @click="submit">
          <span v-if="isBusy" class="cm-spinner"></span>
          {{ isBusy ? "در حال ثبت..." : isEditing ? "ویرایش نظر" : "ثبت نظر" }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import axios from "axios"
import vSelect from "vue-select"
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue"
import MasterPicker from "@/views/components/MasterActiveCourse/MasterPicker.vue"
import LessonPicker from "@/views/components/Lessons/LessonPicker.vue"
import LessonCategoryPicker from "@/views/components/LessonCategories/LessonCategoryPicker.vue"
import FileUploader from "@/views/components/Utils/CustomUploadMedia.vue"
import { BModal, BForm, BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BButton, BFormCheckbox } from "bootstrap-vue"

export default {
  name: "CommentCreateEditModal",
  model: { prop: "value", event: "input" },
  components: {
    vSelect,
    MasterPicker,
    LessonPicker,
    LessonCategoryPicker,
    FileUploader,
    BModal,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BFormCheckbox,
  },
  props: {
    value: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    editingItem: { type: Object, default: null },
    commentTypeOptions: { type: Array, default: () => [] },
    mediaTypeOptionsUI: { type: Array, default: () => [] },
    // با باز شدن، مستقیم به بخش «شکل نهایی» اسکرول شود (کلیک روی برچسب مدیا)
    scrollToPreview: { type: Boolean, default: false },
  },
  data() {
    return {
      localSubmitting: false,
      commentKinds: [
        { text: "متن", value: null },
        { text: "تصویر", value: 2 },
        { text: "صوت", value: 3 },
        { text: "ویدیو", value: 4 },
      ],
      isHydrating: false,
      showPicker: false,
      form: this.emptyForm(),
      avatar: this.emptyUploadState(),
      media: this.emptyUploadState(),
          errors: this.emptyErrors(),
          avatarPresets: [
            "https://api.ferman.org/api/Uploader/Get/5faf03c3-a14f-46ba-bafd-dd8fb928edfb",
            "https://api.ferman.org/api/Uploader/Get/80cd4b14-fb4c-4fa2-aece-4258e4fba2b9",
            "https://api.ferman.org/api/Uploader/Get/49bfe3d3-d272-4a74-bb8a-23ba0df2e20e",
            "https://api.ferman.org/api/Uploader/Get/9b300da9-8399-478e-9828-793f32e9cd2d",
          ],
    }
  },
  computed: {
    isEditing() {
      return !!(this.editingItem && Number(this.editingItem.commentId || 0))
    },
    isBusy() {
      return !!(this.busy || this.localSubmitting || this.avatar.isBtnsDisabled || this.media.isBtnsDisabled)
    },
    // طول دور دایره (r=30) ضربدر درصد باقی‌مانده
    avatarRingOffset() {
      const circumference = 2 * Math.PI * 30
      return circumference * (1 - Math.min(100, Number(this.avatar.uploaded || 0)) / 100)
    },
    objectFieldLabel() {
      const t = Number(this.form.type)
      if (t === 3) return "کدام درس؟"
      if (t === 6) return "کدام پکیج؟"
      return "کدام استاد؟"
    },
    canUploadMedia() {
      return [2, 3, 4].includes(Number(this.form.mediaType))
    },
    fileAccept() {
      const mt = Number(this.form.mediaType)
      if (mt === 2) return "image/*"
      if (mt === 3) return "audio/*"
      if (mt === 4) return "video/*"
      return ""
    },
    mediaLabel() {
      const mt = Number(this.form.mediaType)
      if (mt === 2) return "تصویر"
      if (mt === 3) return "صوت"
      if (mt === 4) return "ویدئو"
      return "-"
    },
    mediaSubtitle() {
      if (!this.canUploadMedia) return "نوع مدیا را انتخاب کنید"
      return this.mediaLabel
    },
    uploaderFileTypeForMedia() {
      const mt = Number(this.form.mediaType)
      if (mt === 2) return 4
      if (mt === 3) return 3
      if (mt === 4) return 3
      return 0
    },
    uploaderFileTypeForAvatar() {
      return 4
    },
    selectedObjectLabel() {
      const t = Number(this.form.type)
      const id = Number(this.form.objectId || 0)
      if (!t || !id) return ""
      const title = String(this.form.objectTitle || "").trim()
      if (title) return title
      if (t === 5) return "استاد"
      if (t === 6) return "پکیج"
      if (t === 3) return "درس"
      return `#${id}`
    },
    submitDisabled() {
      if (this.isBusy) return true
      return false
    },
    effectiveMediaUrl() {
      return String(this.form.mediaUrl || "").trim()
    },
    effectiveMediaType() {
      const url = this.effectiveMediaUrl
      if (!url) return 1
      const mt = Number(this.form.mediaType || 0)
      return [2, 3, 4].includes(mt) ? mt : 2
    },
    effectiveMediaLabel() {
      const mt = Number(this.effectiveMediaType)
      if (mt === 2) return "تصویر"
      if (mt === 3) return "صوت"
      if (mt === 4) return "ویدئو"
      return "-"
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        this.$nextTick(() => {
          if (!this.$refs.cmModal) return
          if (v) this.$refs.cmModal.show()
          else this.$refs.cmModal.hide()
        })
      },
    },
    busy(v) {
      if (!v) this.localSubmitting = false
    },
    editingItem: {
      immediate: true,
      handler(v) {
        if (!v) {
          this.resetAll()
          return
        }
        this.hydrateFromItem(v)
      },
    },
    "form.type"() {
      if (this.isHydrating) return
      this.showPicker = true
      this.clearObjectSelection()
    },
    "form.mediaType"() {
      if (this.isHydrating) return
      this.errors.mediaType = ""
      // فایل قبلی با نوع جدید جور نیست
      this.form.mediaUrl = ""
      this.resetMedia()
    },
    "form.firstName"() {
      if (this.errors.firstName) this.errors.firstName = ""
    },
    "form.message"() {
      if (this.errors.content) this.errors.content = ""
    },
    "form.mediaUrl"() {
      if (this.errors.content) this.errors.content = ""
      if (this.errors.mediaType) this.errors.mediaType = ""
    },
  },
  methods: {
    emptyForm() {
      return {
        firstName: "",
        lastName: "",
        imageUser: "",
        message: "",
        mediaUrl: "",
        objectId: 0,
        objectTitle: "",
        type: 5,
        mediaType: null,
        isVip: false,
        priority: 0,
      }
    },
    emptyErrors() {
      return { firstName: "", lastName: "", type: "", objectId: "", content: "", mediaType: "", priority: "" }
    },
    emptyUploadState() {
      return {
        file: null,
        uploaded: 0,
        uploadedBytes: 0,
        totalSize: 0,
        speedSpan: 0,
        etaSpan: 0,
        done: null,
        errorAlert: false,
        isBtnsDisabled: false,
        _lastTime: 0,
        _lastLoaded: 0,
      }
    },
    resetAll() {
      this.localSubmitting = false
      this.isHydrating = false
      this.showPicker = true
      this.form = this.emptyForm()
      this.avatar = this.emptyUploadState()
      this.media = this.emptyUploadState()
      this.errors = this.emptyErrors()
    },
    // کلیک روی برچسب مدیا: تا انتهای مودال (شکل نهایی) اسکرول شود؛
    // بعد از بار شدن تصویر/ویدیو ارتفاع بیشتر می‌شود، پس یک بار دیگر هم اسکرول می‌شود
    onShown() {
      if (!this.scrollToPreview || !this.$refs.previewSection) return
      const scroller = this.$refs.previewSection.closest(".modal")
      const toBottom = () => {
        if (scroller) scroller.scrollTop = scroller.scrollHeight
      }
      toBottom()
      setTimeout(toBottom, 400)
    },
    onHidden() {
      this.$emit("input", false)
      this.resetAll()
      this.$emit("hidden")
    },
    closeModal() {
      if (this.isBusy) return
      this.$emit("input", false)
      if (this.$refs.cmModal) this.$refs.cmModal.hide()
    },
    pickId(obj, keys) {
      for (const k of keys) {
        const n = Number(obj?.[k] ?? 0)
        if (Number.isFinite(n) && n > 0) return n
      }
      return 0
    },
    pickTitle(obj, keys) {
      for (const k of keys) {
        const t = String(obj?.[k] ?? "").trim()
        if (t) return t
      }
      return ""
    },
    normalizeType(t) {
      const n = Number(t || 0)
      return [3, 5, 6].includes(n) ? n : 5
    },
    hydrateFromItem(item) {
      this.isHydrating = true
      this.errors = this.emptyErrors()
      this.showPicker = false

      const type = this.normalizeType(item?.type || 5)
      const objectId = Number(item?.objectId || 0) || 0
      const objectTitle = String(item?.objectTitle || "").trim() || ""

      const mt = Number(item?.mediaType || 1)
      const safeMt = [1, 2, 3, 4].includes(mt) ? mt : 1

      this.form = {
        firstName: String(item?.firstName || ""),
        lastName: String(item?.lastName || ""),
        imageUser: String(item?.imageUser || ""),
        message: String(item?.message || ""),
        mediaUrl: String(item?.mediaUrl || ""),
        objectId,
        objectTitle,
        type,
        mediaType: safeMt === 1 ? null : safeMt,
        isVip: Boolean(item?.isVip ?? item?.IsVip ?? false),
        priority: Number.isFinite(Number(item?.priority ?? item?.Priority ?? 0)) ? Number(item?.priority ?? item?.Priority ?? 0) : 0,
      }

      this.$nextTick(() => {
        this.isHydrating = false
      })
    },
    clearObjectSelection() {
      this.form.objectId = 0
      this.form.objectTitle = ""
      this.errors.objectId = ""
      this.showPicker = true
    },
    setSelectedMaster(master) {
      const id = this.pickId(master, ["userId", "id", "masterId", "teacherId"])
      const title = this.pickTitle(master, ["fullName", "name", "title"])
      this.form.objectId = id
      this.form.objectTitle = title || (id ? `استاد ${id}` : "")
      this.showPicker = false
      if (id) this.errors.objectId = ""
    },
    setSelectedLesson(payload) {
      const id = typeof payload === "number" ? Number(payload) : this.pickId(payload, ["lessonId", "id", "value", "courseLessonId"])
      const title = typeof payload === "number" ? "" : this.pickTitle(payload, ["title"])
      this.form.objectId = id
      this.form.objectTitle = title || (id ? `درس ${id}` : "")
      this.showPicker = false
      if (id) this.errors.objectId = ""
    },
    setSelectedPackageCategory(payload) {
      const id = typeof payload === "number" ? Number(payload) : this.pickId(payload, ["id", "value", "lessonCategoryId", "categoryId", "packageId"])
      const title = typeof payload === "number" ? "" : this.pickTitle(payload, ["title"])
      this.form.objectId = id
      this.form.objectTitle = title || (id ? `پکیج ${id}` : "")
      this.showPicker = false
      if (id) this.errors.objectId = ""
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === "danger" ? "AlertTriangleIcon" : "CheckIcon",
          variant,
        },
      })
    },
    validate(showToast = true) {
      const base = this.editingItem || {}
      const errs = this.emptyErrors()

      const firstName = String(this.form.firstName || "").trim()
      const type = this.normalizeType(this.form.type)
      const objectId = Number(this.form.objectId || 0)
      const msg = String(this.form.message || "").trim()
      const mediaUrl = String(this.form.mediaUrl || "").trim()

      const pr = Number.isFinite(Number(this.form.priority || 0)) ? Number(this.form.priority || 0) : null

      if (!firstName) errs.firstName = "نام الزامی است."
      if (![3, 5, 6].includes(Number(type))) errs.type = "موضوع معتبر نیست."
      if (!objectId) errs.objectId = "انتخاب استاد/درس/پکیج الزامی است."
      if (!msg && !mediaUrl) errs.content = "حداقل یکی از متن یا مدیا باید وارد شود."

      if (mediaUrl) {
        const mt = Number(this.form.mediaType || 0)
        if (![2, 3, 4].includes(mt)) errs.mediaType = "برای مدیا، نوع مدیا الزامی است."
      }

      if (pr === null || !Number.isInteger(pr) || pr < 0) errs.priority = "اولویت باید عدد صحیح بزرگتر مساوی صفر باشد."

      this.errors = errs

      const ok = !Object.values(errs).some(Boolean)
      if (!ok && showToast) {
        const firstErr = Object.values(errs).find((x) => !!x)
        this.toast(firstErr || "اطلاعات را کامل کنید.", "danger")
      }
      return ok
    },
    onAvatarSelect(file) {
      this.avatar.file = file || null
      this.avatar.done = null
      this.avatar.errorAlert = false
    },
    onMediaSelect(file) {
      this.media.file = file || null
      this.media.done = null
      this.media.errorAlert = false
      if (file && !this.canUploadMedia) {
        this.toast("ابتدا نوع مدیا را انتخاب کنید.", "danger")
      }
    },
    resetAvatar() {
      this.avatar = { ...this.emptyUploadState(), file: null }
    },
    resetMedia() {
      this.media = { ...this.emptyUploadState(), file: null }
    },
    removeAvatarUploaded() {
      if (this.isBusy) return
      this.form.imageUser = ""
      this.resetAvatar()
    },

    pickAvatar() {
      if (this.isBusy || !this.$refs.avatarInput) return
      this.$refs.avatarInput.click()
    },
    onAvatarFile(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ""
      if (!file) return
      this.onAvatarSelect(file)
      this.uploadAvatar()
    },
    selectPreset(url) {
      if (this.isBusy) return
      if (!url) return
      this.form.imageUser = String(url).trim()
      this.resetAvatar()
    },
    // فقط فایل برداشته می‌شود و نوع نظر می‌ماند تا فایل جدید آپلود شود
    clearMediaFile() {
      if (this.isBusy) return
      this.form.mediaUrl = ""
      this.resetMedia()
    },
    buildUploadFormData(file, fileType, meta) {
      const fd = new FormData()
      fd.append("ImageFile", file)
      fd.append("FileType", String(fileType))
      fd.append("Title", String(meta?.title || ""))
      fd.append("Alt", String(meta?.alt || ""))
      fd.append("Description", String(meta?.description || ""))
      return fd
    },
    updateProgress(state, progressEvent) {
      const total = Number(progressEvent?.total || 0)
      const loaded = Number(progressEvent?.loaded || 0)
      if (!total) return

      const now = Date.now()
      const dt = state._lastTime ? Math.max(0.001, (now - state._lastTime) / 1000) : 0
      const dLoaded = state._lastTime ? Math.max(0, loaded - state._lastLoaded) : 0
      const speedBytes = dt ? dLoaded / dt : 0
      const speedKB = speedBytes / 1024

      state.totalSize = total
      state.uploadedBytes = Math.min(loaded, total)
      state.uploaded = (state.uploadedBytes / total) * 100
      state.speedSpan = speedKB > 0 ? speedKB : state.speedSpan

      const remaining = Math.max(0, total - state.uploadedBytes)
      state.etaSpan = speedBytes > 0 ? remaining / speedBytes : state.etaSpan

      state._lastTime = now
      state._lastLoaded = loaded
    },
    async uploadRequest(formData, state) {
      const url = "/Uploader/Upload"
      state.isBtnsDisabled = true
      state.errorAlert = false
      state.done = null
      state.uploaded = 0
      state.uploadedBytes = 0
      state.totalSize = 0
      state.speedSpan = 0
      state.etaSpan = 0
      state._lastTime = 0
      state._lastLoaded = 0

      const res = await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => this.updateProgress(state, e),
      })

      state.uploaded = 100
      state.done = "آپلود با موفقیت انجام شد."
      return res
    },
    extractUploadedUrl(res) {
      const r = res?.data ?? res
      if (r?.url) return String(r.url).trim()
      if (r?.data?.url) return String(r.data.url).trim()
      if (r?.data?.data?.url) return String(r.data.data.url).trim()
      return ""
    },
    async uploadAvatar() {
      try {
        const file = this.avatar.file
        if (!file) return

        const meta = { title: "آواتار", alt: "آواتار", description: "آواتار" }
        const fd = this.buildUploadFormData(file, this.uploaderFileTypeForAvatar, meta)

        const res = await this.uploadRequest(fd, this.avatar)
        const url = this.extractUploadedUrl(res)
        if (!url) {
          this.avatar.errorAlert = true
          this.toast("آپلود آواتار انجام نشد.", "danger")
          return
        }

        this.form.imageUser = url
      } catch (e) {
        this.avatar.errorAlert = true
        this.toast("خطا در آپلود آواتار.", "danger")
      } finally {
        this.avatar.isBtnsDisabled = false
        this.avatar.file = null
      }
    },
    async uploadMedia() {
      try {
        if (!this.canUploadMedia) {
          this.toast("نوع مدیا را انتخاب کنید.", "danger")
          return
        }

        const file = this.media.file
        if (!file) return

        const fileType = this.uploaderFileTypeForMedia
        if (!fileType) return

        const meta = { title: this.mediaLabel, alt: this.mediaLabel, description: this.mediaLabel }
        const fd = this.buildUploadFormData(file, fileType, meta)

        const res = await this.uploadRequest(fd, this.media)
        const url = this.extractUploadedUrl(res)
        if (!url) {
          this.media.errorAlert = true
          this.toast("آپلود فایل انجام نشد.", "danger")
          return
        }

        this.form.mediaUrl = url
        this.errors.mediaType = ""
      } catch (e) {
        this.media.errorAlert = true
        this.toast("خطا در آپلود فایل.", "danger")
      } finally {
        this.media.isBtnsDisabled = false
        this.media.file = null
      }
    },
    buildSubmitPayload() {
      const base = this.editingItem || {}
      const type = this.normalizeType(this.form.type)
      const objectId = Number(this.form.objectId || 0)

      const firstName = String(this.form.firstName || "").trim()
      const lastName = String(this.form.lastName || "").trim()
      const imageUser = String(this.form.imageUser || "").trim()
      const message = String(this.form.message || "").trim()
      const mediaUrl = String(this.form.mediaUrl || "").trim()
      const mediaType = mediaUrl ? Number(this.form.mediaType || 0) : 1

      return {
        commentId: this.isEditing ? Number(base.commentId || 0) : 0,
        parentId: this.isEditing ? Number(base.parentId || 0) : 0,
        firstName,
        lastName,
        imageUser,
        message,
        objectId,
        type,
        mediaType: [1, 2, 3, 4].includes(Number(mediaType)) ? Number(mediaType) : 1,
        createDate: this.isEditing ? (base.createDate || new Date().toISOString()) : new Date().toISOString(),
        userId: this.isEditing ? Number(base.userId || 0) : 0,
        mediaUrl,
        objectTitle: this.form.objectTitle || "",
        isVip: !!this.form.isVip,
        priority: Number.isFinite(Number(this.form.priority || 0)) ? Number(this.form.priority || 0) : 0,
      }
    },
    submit() {
      if (this.isBusy) return
      const ok = this.validate(true)
      if (!ok) return
      this.localSubmitting = true
      this.$emit("submit", this.buildSubmitPayload())
    },
  },
}
</script>

<style scoped>
.comment-modal :deep(.modal-content) {
  border: 0;
  border-radius: 16px;
  box-shadow: 0 22px 70px rgba(0, 0, 0, 0.22);
  overflow: hidden;
  direction: rtl;
}

.cm-block + .cm-block {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
}
.cm-block__title {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 900;
  color: #111827;
}
.cm-avatar-upload {
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 2px dashed #c4c4d0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7367f0;
  cursor: pointer;
  transition: border-color 0.2s ease;
}
.cm-avatar-upload:hover,
.cm-avatar-upload:focus-visible {
  outline: none;
  border-color: #7367f0;
}
.cm-avatar-upload.has-image {
  border-style: solid;
  border-color: transparent;
}
.cm-avatar-upload .cm-avatar-img {
  width: 100%;
  height: 100%;
}
.cm-avatar-upload__icon {
  width: 20px;
  height: 20px;
}
/* حلقه‌ی پیشرفت آپلود که دور دایره می‌چرخد */
.cm-avatar-ring {
  position: absolute;
  inset: -4px;
  width: calc(100% + 8px);
  height: calc(100% + 8px);
  transform: rotate(-90deg);
  animation: cmring 1.2s linear infinite;
}
.cm-avatar-ring__track {
  fill: none;
  stroke: rgba(115, 103, 240, 0.15);
  stroke-width: 3;
}
.cm-avatar-ring__bar {
  fill: none;
  stroke: #7367f0;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 188.5;
  transition: stroke-dashoffset 0.2s ease;
}
@keyframes cmring {
  from { transform: rotate(-90deg); }
  to { transform: rotate(270deg); }
}
@media (prefers-reduced-motion: reduce) {
  .cm-avatar-ring {
    animation: none;
  }
}
/* آپلودکننده داخل مودال فشرده‌تر */
.cm-sections :deep(.fu-head) {
  padding: 6px 10px;
}
.cm-sections :deep(.fu-body) {
  padding: 8px 10px;
}
.cm-sections :deep(.fu-actions) {
  margin-top: 8px;
}
.cm-sections :deep(.fu-btn) {
  height: 34px;
  border-radius: 10px;
}
.cm-user-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.cm-user-row__field {
  flex: 1 1 0;
  min-width: 0;
}
.cm-kind {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  padding: 4px;
  border-radius: 12px;
  background: #f3f4f6;
}
.cm-kind__btn {
  height: 32px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  color: #4b5563;
}
.cm-kind__btn--active {
  background: #7367f0;
  color: #fff;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.3);
}
.cm-current-media {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border: 1px dashed #e5e7eb;
  border-radius: 12px;
}
.cm-current-media__hint {
  flex: 1 1 auto;
  font-size: 12px;
  color: #6b7280;
}
.cm-current-media .cm-preview__remove {
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.4);
  background: transparent;
}
.cm-final {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 14px;
}
.cm-final__head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cm-final__avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
}
.cm-final__name {
  font-weight: 800;
  font-size: 14px;
  color: #111827;
}
.cm-final__vip {
  margin-inline-start: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #ff9f43;
  color: #fff;
  font-size: 10px;
}
.cm-final__meta {
  font-size: 12px;
  color: #6b7280;
}
.cm-final__media {
  margin-top: 12px;
}
.cm-final__media img,
.cm-final__media video {
  display: block;
  width: 100%;
  max-height: 200px;
  object-fit: contain;
  border-radius: 12px;
  background: #000;
}
.cm-final__text {
  margin: 12px 0 0;
  font-size: 13px;
  line-height: 1.9;
  white-space: pre-line;
  color: #374151;
}
.cm-title {
  font-size: 16px;
  font-weight: 900;
  color: #111827;
  margin-bottom: 0;
}
.cm-grid {
  margin-top: 2px;
}
.cm-group :deep(.form-group) {
  margin-bottom: 0 !important;
}
.cm-label {
  font-size: 12px;
  font-weight: 800;
  color: #374151;
  margin-bottom: 4px;
  min-height: 18px;
  display: flex;
  align-items: center;
}

.cm-control {
  height: 38px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 10px 12px;
}
.cm-textarea {
  height: auto;
  min-height: 64px;
  resize: vertical;
}

.cm-control--invalid {
  border-color: #ef4444 !important;
}

.cm-error {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 800;
  color: #ef4444;
}

.cm-vselect :deep(.vs__dropdown-toggle) {
  height: 38px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 2px 10px;
  display: flex;
  align-items: center;
}

.cm-subject-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  align-items: start;
}
@media (min-width: 768px) {
  .cm-subject-row {
    grid-template-columns: 140px minmax(0, 1fr) 90px auto;
  }
}
/* انتخابگرها عنوان خودشان را دارند؛ اینجا عنوان «کدام …؟» کافی است */
.cm-subject-row__object :deep(label),
.cm-subject-row__object :deep(legend) {
  display: none;
}
.cm-subject-row__object :deep(.form-group),
.cm-subject-row__object :deep(.row) {
  margin: 0;
}
.cm-subject-row__object :deep(.col-12),
.cm-subject-row__object :deep([class*="col-"]) {
  padding: 0;
}
.cm-subject-row__object.is-invalid :deep(.vs__dropdown-toggle),
.cm-subject-row__object.is-invalid .cm-picker-selected {
  border-color: #ef4444;
}
.cm-subject-row__vip {
  display: flex;
  flex-direction: column;
}
.cm-subject-row__vip :deep(.custom-switch) {
  height: 38px;
  display: flex;
  align-items: center;
}
.cm-hint {
  font-size: 12px;
  color: #6b7280;
}

.cm-footer {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.cm-btn {
  height: 38px;
  border-radius: 12px;
  font-weight: 900;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}
.cm-btn--success {
  box-shadow: 0 10px 24px rgba(16, 185, 129, 0.22);
}
.cm-btn--ghost {
  background: transparent;
}

.cm-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.55);
  border-top-color: rgba(255, 255, 255, 1);
  border-radius: 999px;
  display: inline-block;
  animation: cmspin 0.9s linear infinite;
}
@keyframes cmspin {
  to {
    transform: rotate(360deg);
  }
}
.cm-preview__remove {
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
}
.cm-chip {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.cm-avatar-box {
  position: relative;
  width: 56px;
  height: 56px;
}
.cm-avatar-img {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
  background: #fff;
}
.cm-avatar-placeholder {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cm-avatar-placeholder__icon {
  font-size: 22px;
  opacity: 0.65;
}
.cm-preview-close {
  position: absolute;
  top: -8px;
  left: -8px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  font-size: 16px;
  line-height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cm-picker-selected {
  height: 38px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.cm-picker-selected__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  font-weight: 900;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cm-picker-selected__clear {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  font-size: 18px;
  line-height: 24px;
  cursor: pointer;
  flex: 0 0 auto;
}

.cm-avatar-presets {
  margin-top: 8px;
}
.cm-presets-title {
  font-size: 11px;
  color: #6b7280;
  font-weight: 800;
}
.cm-presets {
  display: flex;
  gap: 8px;
  margin-top: 6px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 0 4px 0;
  justify-content: flex-start;
  align-items: center;
}
.cm-preset-img {
  cursor: pointer;
  display: block;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
}
.cm-preset--selected {
  box-shadow: 0 0 0 3px rgba(16,185,129,0.16);
  border-color: #10b981;
}
.cm-preset--disabled {
  opacity: 0.5;
  pointer-events: none;
}

/* تم تاریک (کلاس dark-layout روی body)؛ رنگ‌ها هم‌خوان با کارت‌ها و ورودی‌های پنل */
.dark-layout .cm-title,
.dark-layout .cm-picker-selected__text {
  color: #d0d2d6;
}
.dark-layout .cm-label {
  color: #b4b7bd;
}
.dark-layout .cm-hint,
.dark-layout .cm-presets-title {
  color: #8e94a3;
}
.dark-layout .cm-control,
.dark-layout .cm-vselect :deep(.vs__dropdown-toggle) {
  border-color: #404656;
}
.dark-layout .cm-picker-selected {
  background: #161d31;
  border-color: #404656;
}
.dark-layout .cm-picker-selected__clear,
.dark-layout .cm-preview-close {
  background: #3b4253;
  border-color: #404656;
  color: #d0d2d6;
}
.dark-layout .cm-avatar-img,
.dark-layout .cm-avatar-placeholder {
  background: #161d31;
  border-color: #404656;
}
.dark-layout .cm-block + .cm-block {
  border-top-color: #3b4253;
}
.dark-layout .cm-block__title {
  color: #d0d2d6;
}
.dark-layout .cm-kind {
  background: #161d31;
}
.dark-layout .cm-kind__btn {
  color: #b4b7bd;
}
.dark-layout .cm-kind__btn--active {
  color: #fff;
}
.dark-layout .cm-current-media,
.dark-layout .cm-final {
  border-color: #404656;
}
.dark-layout .cm-final__name {
  color: #d0d2d6;
}
.dark-layout .cm-final__text {
  color: #b4b7bd;
}
</style>

<style>
/* سربرگ، بدنه و پانویس مودال نظر (BModal آن‌ها را بیرون از scope رندر می‌کند) */
.comment-modal .modal-header {
  padding: 10px 18px 2px;
  border-bottom: 0;
}
.comment-modal .modal-body {
  padding: 6px 18px;
}
.comment-modal .modal-footer {
  padding: 6px 18px 12px;
  border-top: 0;
}
</style>
